import { ComponentStyleConfig } from '@chakra-ui/react';

import { bodyStyles } from '../../common';
import { createVariables } from '../../../util';
import { getColorVar, SystemStyleObject } from '@chakra-ui/theme-tools';
import { z } from 'zod';
import { merge } from 'lodash';

const propsSchema = z.object({
  theme: z.record(z.any()),
  status: z.enum(['info', 'success', 'warning', 'error']).catch('info'),
});

const withKnownProps =
  <T>(f: (props: z.output<typeof propsSchema>) => T) =>
  (props: unknown) =>
    f(propsSchema.parse(props));

const variables = createVariables<'base'>();

const parts = ['container', 'title', 'description', 'icon'] as const;

const subtle = ({ theme, status }: z.output<typeof propsSchema>) => {
  const stateColor = (n: number) => {
    const percentage = `${Math.min(Math.max(0, n), 1) * 100}%`;
    return `color-mix(in srgb, ${getColorVar(theme, `state.${status}`)} ${percentage}, transparent)`;
  };
  return {
    container: {
      backgroundColor: 'layer.1',
      backgroundImage: `radial-gradient(circle at top left, ${stateColor(0.33)}, ${stateColor(0.08)} 50px, transparent 100px)`,
      border: 'solid 1px',
      borderColor: `var(${variables.base})`,
      color: `color-mix(in srgb, ${getColorVar(theme, 'text.primary')} 60%, transparent)`,
    },
    title: {
      color: 'text.primary',
    },
    icon: {
      color: `text.${status}`,
    },
  } satisfies Partial<Record<(typeof parts)[number], SystemStyleObject>>;
};

export const AlertStyles: ComponentStyleConfig = {
  parts: [...parts],
  baseStyle: withKnownProps(
    ({ theme, status }) =>
      ({
        container: {
          [variables.base]: getColorVar(theme, `state.${status}`),
          borderRadius: '8px',
          background: 'layer.0',
        },
        title: {
          ...bodyStyles['body-md-bold'],
        },
        description: {
          ...bodyStyles['body-md'],
        },
        icon: {
          color: 'inherit',
          alignSelf: 'flex-start',
          width: 5,
          height: 5,
          '& svg': {
            marginTop: 0,
          },
        },
      }) satisfies Record<(typeof parts)[number], unknown>,
  ),
  variants: {
    solid: {
      container: {
        background: `var(${variables.base})`,
        color: 'button.text.primary',
      },
    } satisfies Partial<Record<(typeof parts)[number], unknown>>,
    subtle: withKnownProps(subtle),
    'left-accent': withKnownProps((props) => {
      const style = subtle(props);
      return {
        ...style,
        container: {
          ...style.container,
          borderLeftWidth: '4px',
        },
      } satisfies Partial<Record<(typeof parts)[number], unknown>>;
    }),
    'top-accent': withKnownProps((props) => {
      const style = subtle(props);
      return merge(style, {
        container: {
          '&&': {
            borderTopWidth: '4px',
          },
        },
      }) satisfies Partial<Record<(typeof parts)[number], unknown>>;
    }),
  },
  defaultProps: {
    variant: 'left-accent',
  },
};
